import { Button } from '@base-ui/react/button';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import buttonStyles from '~/styles/button.module.css';
import { ErrorText, FieldHint } from './Form';

export type MediaImage = { key: string; url: string };

// Mirrors `store_content_image`; checked here too so a wrong file fails at once.
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_BYTES = 8 * 1024 * 1024;

export const FORMAT_HINT = 'JPEG, PNG, GIF or WebP, up to 8 MB.';

function imageFileProblem(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type))
    return "It isn't a JPEG, PNG, GIF or WebP image.";
  if (file.size > MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `It's ${mb} MB, and the limit is 8 MB.`;
  }
  return null;
}

// Uploads through `admin_upload_media`. `key` is what gets saved; `url` previews.
export function useMediaUpload(onUploaded: (image: MediaImage) => void) {
  const [problem, setProblem] = useState<string | null>(null);
  // Named in the error, so it's clear which pick failed.
  const [fileName, setFileName] = useState('');
  const mutation = api.adminUploadMedia.useMutation(undefined, {
    meta: { toast: false },
    onSuccess: (res) => onUploaded({ key: res.key, url: res.url }),
  });

  const upload = (file: File) => {
    const local = imageFileProblem(file);
    setFileName(file.name);
    setProblem(local);
    if (local) return;
    mutation.mutate({ body: { file } });
  };

  const reason = mutation.isError
    ? (apiErrorDetail(mutation.error) ?? 'Please try again.')
    : problem;
  const error = reason && `Couldn't upload "${fileName}". ${reason}`;

  const clearError = () => {
    setProblem(null);
    mutation.reset();
  };

  return { upload, uploading: mutation.isPending, error, clearError };
}

// One image field: preview, upload, replace and remove. `value.key` is "" when
// there is no image; removing sets it so the save clears the column.
export function MediaPicker({
  id,
  value,
  onChange,
  onUploadingChange,
  alt,
  wide = false,
}: {
  id: string;
  value: MediaImage;
  onChange: (image: MediaImage) => void;
  // Lets the form hold its save while an upload is in flight, or the old key
  // would be saved and the picked image silently dropped.
  onUploadingChange?: (uploading: boolean) => void;
  alt: string;
  // A banner-shaped preview, for cover images.
  wide?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const { upload, uploading, error, clearError } = useMediaUpload(onChange);
  useEffect(
    () => onUploadingChange?.(uploading),
    [uploading, onUploadingChange],
  );

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires.
    e.target.value = '';
    if (file) upload(file);
  };

  const hasImage = value.key !== '';

  return (
    <Picker>
      {hasImage && value.url && (
        <Frame $wide={wide}>
          <img src={value.url} alt={alt} />
        </Frame>
      )}
      <input
        ref={input}
        id={id}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFile}
        hidden
      />
      <Actions>
        <Button
          className={buttonStyles.Button}
          disabled={uploading}
          onClick={() => {
            clearError();
            input.current?.click();
          }}
        >
          {hasImage ? 'Replace image' : 'Upload image'}
        </Button>
        {hasImage && (
          <Button
            className={buttonStyles.Button}
            disabled={uploading}
            onClick={() => {
              clearError();
              onChange({ key: '', url: '' });
            }}
          >
            Remove
          </Button>
        )}
      </Actions>
      {!uploading && error && <ErrorText role="alert">{error}</ErrorText>}
      <FieldHint>{uploading ? 'Uploading...' : FORMAT_HINT}</FieldHint>
    </Picker>
  );
}

const Picker = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

// Fixed shape + object-fit cover so varied images look uniform.
export const Frame = styled.div<{ $wide?: boolean }>`
  background-color: var(--grey-75);
  border: 1px solid var(--grey-200);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  display: inline-block;
  padding: 0.375rem;

  img {
    background-color: var(--grey-100);
    border-radius: 5px;
    display: block;
    height: 135px;
    object-fit: cover;
    max-width: 100%;
    width: ${(p) => (p.$wide ? '480px' : '240px')};
  }
`;
